/**
 * Modal / Dialog management component
 *
 * Primary surfaces:
 *   - showConfirmModal()  → native <dialog> confirmation, promise<boolean>
 *   - showPromptModal()   → native <dialog> text input, promise<string|null>
 *   - createModal/showModal/openModal → dynamic ad-hoc panels (legacy)
 */

export function openModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.classList.add('active');
    }
}

export function closeModalById(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.classList.remove('active');
    }
}

/**
 * Build an ad-hoc centered panel. Kept for callers that want a
 * lightweight container without the confirm/prompt affordances.
 */
export function createModal(options) {
    const { id, title, content, closable = true } = options;

    const modal = document.createElement('div');
    modal.id = id;
    modal.className = 'modal dynamic-modal active';

    modal.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h3>${title}</h3>
                ${closable ? `<button class="close-modal" aria-label="Close">&times;</button>` : ''}
            </div>
            <div class="modal-body">${content}</div>
        </div>
    `;

    if (closable) {
        modal.querySelector('.close-modal').addEventListener('click', () => modal.remove());
        modal.addEventListener('click', (e) => {
            if (e.target === modal) modal.remove();
        });
    }

    return modal;
}

export function showModal(options) {
    const modal = createModal(options);
    document.body.appendChild(modal);
    return modal;
}

/**
 * Global Escape handler — closes any non-native ad-hoc modals.
 * Native <dialog> handles its own Escape.
 */
export function setupEscapeHandler() {
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        document.querySelectorAll('.dynamic-modal').forEach(m => m.remove());
    });
}

/**
 * Styled confirmation dialog. Native <dialog> — free focus trap + Escape.
 * @returns {Promise<boolean>}
 */
export function showConfirmModal(options) {
    const {
        title = 'Confirm',
        message = '',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        confirmClass = 'btn-primary',
        icon = null
    } = options;

    return new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'app-dialog';
        dialog.innerHTML = `
            <div class="app-dialog-header">
                <h3>${icon ? `<span class="app-dialog-icon">${icon}</span>` : ''}<span>${title}</span></h3>
            </div>
            <div class="app-dialog-body">${message}</div>
            <div class="app-dialog-footer">
                <button type="button" class="btn btn-secondary" data-role="cancel">${cancelText}</button>
                <button type="button" class="btn ${confirmClass}" data-role="confirm">${confirmText}</button>
            </div>
        `;

        const finish = (result) => {
            if (dialog.open) dialog.close();
            dialog.remove();
            resolve(result);
        };

        dialog.querySelector('[data-role="cancel"]').addEventListener('click', () => finish(false));
        dialog.querySelector('[data-role="confirm"]').addEventListener('click', () => finish(true));
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); finish(false); });
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) finish(false); // backdrop click
        });

        document.body.appendChild(dialog);
        dialog.showModal();
        dialog.querySelector('[data-role="confirm"]').focus();
    });
}

/**
 * Styled text-prompt dialog.
 * @returns {Promise<string|null>}
 */
export function showPromptModal(options) {
    const {
        title = 'Input',
        message = '',
        placeholder = '',
        defaultValue = '',
        confirmText = 'OK',
        cancelText = 'Cancel',
        icon = null
    } = options;

    return new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'app-dialog';
        dialog.innerHTML = `
            <div class="app-dialog-header">
                <h3>${icon ? `<span class="app-dialog-icon">${icon}</span>` : ''}<span></span></h3>
            </div>
            <div class="app-dialog-body">
                ${message ? `<p class="app-dialog-message"></p>` : ''}
                <input type="text" class="app-dialog-input" />
            </div>
            <div class="app-dialog-footer">
                <button type="button" class="btn btn-secondary" data-role="cancel">${cancelText}</button>
                <button type="button" class="btn btn-primary" data-role="confirm">${confirmText}</button>
            </div>
        `;

        // Fill text via textContent to avoid XSS from messages/titles
        dialog.querySelector('.app-dialog-header h3 span:last-child').textContent = title;
        if (message) dialog.querySelector('.app-dialog-message').textContent = message;

        const input = dialog.querySelector('.app-dialog-input');
        input.placeholder = placeholder;
        input.value = defaultValue;

        const finish = (result) => {
            if (dialog.open) dialog.close();
            dialog.remove();
            resolve(result);
        };

        dialog.querySelector('[data-role="cancel"]').addEventListener('click', () => finish(null));
        dialog.querySelector('[data-role="confirm"]').addEventListener('click', () => finish(input.value || null));
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); finish(input.value || null); }
        });
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); finish(null); });
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) finish(null);
        });

        document.body.appendChild(dialog);
        dialog.showModal();
        input.focus();
        input.select();
    });
}
